// ============================================================
// authDenial — single-shot global denial classification + redirect
// ============================================================
// Every protected API response flows through here (via useAuthFetch). A denial
// is classified by HTTP status + the backend `code` field, mapped to a redirect
// target (or left inline), and fired AT MOST ONCE per burst of responses.
//
// Design split:
//   • classifyDenial / redirectTargetForDenial / decideRedirect are PURE and
//     fully unit-tested — no DOM, no navigation, no module state.
//   • handleDenialResponse is the side-effecting adapter. It reads a CLONE of
//     the response so the caller's body stays intact, then asks decideRedirect
//     what to do and fires the single-shot navigation through an injectable
//     sink (defaults to a full-document navigation).
//
// The single-shot latch is released ONLY by the real full-document navigation
// tearing the page down (resetting all module state), or explicitly by
// resetDenialRedirectLatch() in unit tests. It is deliberately NOT self-cleared
// on the next event-loop tick: a burst of qualifying denials does not all
// resolve within one tick (each awaits response.clone().json()), so a
// tick-based release would let later members of the SAME burst each fire their
// own navigation — observed in CI as a double redirect to /access-pending.

export type DenialKind =
  | 'unauthenticated' // 401
  | 'billing' // 402 — trial or subscription access requires billing recovery
  | 'access_pending' // 403 + ACCESS_PENDING
  | 'admin' // 403 + ADMIN_REQUIRED (inline)
  | 'forbidden' // other 403 (inline)
  | 'other' // everything else (inline)

/** Pure classifier. `code` is the backend `code` field (may be absent/null for
 *  a malformed or non-JSON denial body — classification then falls back to the
 *  status alone). */
export function classifyDenial(status: number, code?: string | null): DenialKind {
  if (status === 401) return 'unauthenticated'
  if (status === 402) return 'billing'
  if (status === 403) {
    if (code === 'ACCESS_PENDING') return 'access_pending'
    if (code === 'ADMIN_REQUIRED') return 'admin'
    return 'forbidden'
  }
  return 'other'
}

/** Pure: the redirect destination for a denial kind, or null when the denial is
 *  handled inline (no navigation). `billing → /offboarded` takes expired or
 *  canceled accounts to the API-driven recovery screen. */
export function redirectTargetForDenial(kind: DenialKind): string | null {
  switch (kind) {
    case 'unauthenticated':
      return '/sign-in'
    case 'billing':
      return '/offboarded'
    case 'access_pending':
      return '/access-pending'
    case 'admin':
    case 'forbidden':
    case 'other':
      return null
  }
}

/** Pure decision: where (if anywhere) a denial of `kind` should navigate, given
 *  the current path. Returns null when the kind is inline, or when the target
 *  equals the current path — the loop guard that also stops /access-pending
 *  from ever redirecting back to itself. */
export function decideRedirect(kind: DenialKind, currentPath: string): string | null {
  const target = redirectTargetForDenial(kind)
  if (!target) return null
  if (currentPath === target) return null
  return target
}

// ── Side-effecting adapter ────────────────────────────────────────────────

export interface DenialHandlerDeps {
  /** Performs the navigation. Default: full-document navigation. */
  navigate: (dest: string) => void
  /** Reads the current path. Default: window.location.pathname. */
  currentPath: () => string
}

function defaultDeps(): DenialHandlerDeps {
  return {
    navigate: (dest) => {
      if (typeof window !== 'undefined') window.location.assign(dest)
    },
    currentPath: () => (typeof window !== 'undefined' ? window.location.pathname : '/'),
  }
}

// Single-shot latch. Guards against a redirect storm when several near-
// simultaneous responses all carry the same denial. Cleared only by a real
// navigation (page teardown) or resetDenialRedirectLatch(): see the file
// header for why it is never released on a timer.
let redirectInFlight = false

/** Test-only: clear the single-shot latch so each case starts clean. */
export function resetDenialRedirectLatch(): void {
  redirectInFlight = false
}

function fireRedirect(kind: DenialKind, deps: DenialHandlerDeps): void {
  if (redirectInFlight) return
  const dest = decideRedirect(kind, deps.currentPath())
  if (!dest) return
  redirectInFlight = true
  deps.navigate(dest)
  // Latch stays set. The real full-document navigation just fired unloads the
  // page and resets this module, which is the ONLY production release path.
  // Unit tests call resetDenialRedirectLatch() between cases. We intentionally
  // do NOT release on a timer — doing so would let a still-in-flight member of
  // the same denial burst redirect a second time (see the file header).
}

/**
 * Inspect a non-OK response and fire at most one redirect for it. Reads a CLONE,
 * so the body the caller reads afterwards is untouched. Never throws: a non-JSON
 * or empty denial body simply classifies by status (code stays null).
 *
 * No-op for OK responses and for inline denial kinds (admin / forbidden / other).
 */
export async function handleDenialResponse(
  response: Response,
  deps: DenialHandlerDeps = defaultDeps()
): Promise<void> {
  if (response.ok) return

  let code: string | null = null
  try {
    const body = await response.clone().json()
    if (body && typeof body.code === 'string') code = body.code
  } catch {
    // Malformed / non-JSON / empty denial body — classify by status alone.
    code = null
  }

  fireRedirect(classifyDenial(response.status, code), deps)
}
